import { PropsWithChildren, useId, useRef, useCallback, JSX } from 'preact/compat'
import clsx from 'clsx'
import useHydrated from 'landing_pages/common/useHydrated'
import PlaceholderInput from './PlaceholderInput'
import Inputmask from 'inputmask'
import { bindStreetAddressAutocomplete, unbindStreetAddressAutocomplete } from 'landing_pages/address_autocomplete'

type InputType = 'email' | 'number' | 'password' | 'tel' | 'text' | 'url'

export type MapsAutocompleteHandler = (result: {
  StreetAddress: string
  City: string
  State: string
  ZIP_Code: string
}) => void

export type NativeTextFieldProps = {
  type?: InputType
  label?: string
  value?: string
  error?: boolean
  disabled?: boolean
  inputMask?: string
  mapsAutoComplete?: MapsAutocompleteHandler
  onInput?: JSX.GenericEventHandler<HTMLInputElement>
  onChange?: JSX.GenericEventHandler<HTMLInputElement>
  onBlur?: JSX.FocusEventHandler<HTMLInputElement>
  id?: string
  name?: string
  placeholder?: string
  showLabel?: boolean
}

export default function NativeTextField({
  type = 'text',
  label,
  value = '',
  error = false,
  disabled = false,
  inputMask,
  mapsAutoComplete,
  onInput,
  onChange,
  onBlur,
  id,
  name,
  placeholder,
  showLabel = true,
}: PropsWithChildren<NativeTextFieldProps>) {
  const hydrated = useHydrated()
  const generatedId = useId()
  const inputId = id || generatedId
  const inputRef = useRef<HTMLInputElement | null>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  // Input mask / maps autocomplete setup
  const setInputRef = useCallback(async (input: HTMLInputElement) => {
    if (inputRef.current) {
      if (inputMask) Inputmask.remove(inputRef.current)
      if (autocompleteRef.current) unbindStreetAddressAutocomplete(autocompleteRef.current)
    }

    inputRef.current = input

    if (inputRef.current) {
      if (inputMask) {
        Inputmask({ mask: inputMask }).mask([inputRef.current])
      }
      if (mapsAutoComplete && typeof window !== 'undefined') {
        autocompleteRef.current = await bindStreetAddressAutocomplete(inputRef.current, mapsAutoComplete) ?? null
      }
    }
  }, [inputMask, mapsAutoComplete])

  if (!hydrated) return <PlaceholderInput marginTop={showLabel ? '25px' : '0px'} />

  return (
    <div className={clsx('native-text-field', 'input-container', 'input-container-text', error && 'error')} style={{ width: '100%' }}>
      {showLabel && (
        <label htmlFor={inputId} style={{ display: 'block', height: '19px', marginTop: 4, marginBottom: 1, cursor: 'text', justifySelf: 'start' }}>
          {label}
        </label>
      )}

      <input
        type={type}
        id={inputId}
        name={name}
        value={value}
        onInput={onInput}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder}
        ref={setInputRef}
        aria-invalid={error || undefined}
        className={error ? 'error' : undefined}
        style={{
          padding: '0.5rem 1rem',
          border: '1px solid #ccc',
          borderRadius: 4,
          fontSize: '1rem',
          boxSizing: 'border-box',
          marginBottom: '0px',
          height: '48px',
        }}
      />
    </div>
  )
}
